// src/components/table/TblSensors.tsx

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { matchSorter } from 'match-sorter'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'

import {
  CellBoilerplate,
  CellDate,
  CellIsActive,
  DefaultColumnFilter,
  FilterByStatus,
  FilterStatus,
  HStack,
} from '..'
import { ASC, DESC, getStorage } from '../../api'

type Props = {
  data: any[]
  onRowClick?: (_: number) => void
  onStateChange: (_: {
    rows: any[]
    page: any[]
    pageIndex: number
    pageSize: number
    pageCount: number
  }) => void
  onUpdateMaxResults?: (_: number) => void
  PAGE_CONFIG?: string
}

export const TblSensors = ({
  data,
  onRowClick,
  onStateChange,
  onUpdateMaxResults,
  PAGE_CONFIG = 'settings_sensors_all',
}: Props) => {
  const [config, _] = useState(getStorage(PAGE_CONFIG))

  const maxResults = config?.maxResults ?? 10
  const orderby = config?.orderby ?? 'id'
  const order = config?.order ?? 'desc'

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), [])

  const filterTypes = useMemo(
    () => ({
      text: (rows, id, filterValue) =>
        matchSorter(rows, filterValue, { keys: [id] }),
      statusFilter: (rows, id, filterValue) => {
        if (filterValue === 'all') return rows
        return rows.filter(row => row.values[id] === filterValue)
      },
    }),
    []
  )
  const initialState = useMemo(
    () => ({
      sortBy: [{ id: orderby, desc: order !== 'ASC' }],
      filters: [{ id: 'folder_id', value: 'true' }],
      pageSize: maxResults,
    }),
    [maxResults, order, orderby]
  )

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        Header: 'ID',
        sortType: (a, b) => Number(a.original.id) - Number(b.original.id),
        width: 80,
      },
      {
        accessor: 'is_active',
        Cell: CellIsActive,
        disableFilters: true,
        disableSortBy: true,
        Header: 'aktiviert',
        width: 80,
      },
      { accessor: 'name', Header: 'Name' },
      { accessor: 'device_id', Header: 'Device ID' },
      { accessor: 'sensor_type', Header: 'Typ' },
      { accessor: 'provider', Cell: CellBoilerplate, Header: 'Provider' },
      { accessor: 'value', Header: 'Wert' },
      { accessor: 'folder_name', Cell: CellBoilerplate, Header: 'Gebäude' },
      {
        accessor: 'status',
        Cell: FilterByStatus,
        Filter: FilterStatus,
        Header: 'Status',
      },
      { accessor: 'time', Cell: CellDate, Header: 'letzte Aktivität' },
    ],
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex, pageSize },
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
    rows,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  // stabile Änderungs-Signaturen für rows/page, damit onStateChange nur bei echten Änderungen feuert
  const rowsSig = useMemo(() => {
    const first = rows[0]?.original?.id ?? rows[0]?.id ?? ''
    const last = rows.at(-1)?.original?.id ?? rows.at(-1)?.id ?? ''
    return `${rows.length}:${first}:${last}`
  }, [rows])

  const pageSig = useMemo(() => {
    const first = page[0]?.original?.id ?? page[0]?.id ?? ''
    const last = page.at(-1)?.original?.id ?? page.at(-1)?.id ?? ''
    return `${page.length}:${first}:${last}`
  }, [page])

  useEffect(() => {
    onStateChange({ rows, page, pageIndex, pageSize, pageCount })
    // absichtlich ohne onStateChange/rows/page als Deps, um nur inhaltliche Änderungen zu berücksichtigen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsSig, pageSig, pageIndex, pageSize, pageCount])

  const onPageChange = useCallback(
    (event, newPage) => gotoPage(newPage),
    [gotoPage]
  )

  const onRowsPerPageChange = useCallback(
    event => {
      const maxResults = Number(event.target.value)
      setPageSize(maxResults)
      onUpdateMaxResults?.(maxResults)
    },
    [setPageSize, onUpdateMaxResults]
  )

  return (
    <>
      <TableContainer>
        <Table sx={{ width: 1 }} {...getTableProps()}>
          <TableHead>
            {headerGroups.map(headerGroup => (
              <>
                <TableRow
                  key={headerGroup.id}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map(column => (
                    <TableCell
                      key={column.id}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      sx={{ fontSize: 16, width: column.width }}
                    >
                      {column.render('Header')}
                      <TableSortLabel
                        active={column.isSorted}
                        direction={column.isSortedDesc ? DESC : ASC}
                      />
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {headerGroup.headers.map(column => (
                    <TableCell
                      key={column.id}
                      sx={{ p: 0 }}
                      {...column.getHeaderProps()}
                    >
                      {column.canFilter ? column.render('Filter') : null}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            ))}
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {page.map(row => {
              const onClick = () => onRowClick?.(row.original.id)

              prepareRow(row)

              return (
                <TableRow
                  key={row.id}
                  onClick={onClick}
                  sx={{ cursor: 'pointer' }}
                  {...row.getRowProps()}
                >
                  {row.cells.map(cell => (
                    <TableCell
                      key={`${row.id}-${cell.column.id}`}
                      {...cell.getCellProps()}
                      sx={{ fontSize: 16 }}
                    >
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <HStack sx={{ width: 1 }}>
        <TablePagination
          component={'div'}
          count={pageCount}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={pageIndex}
          rowsPerPage={Number(pageSize)}
          rowsPerPageOptions={[10, 15, 20, 25, 50, 100]}
        />

        <Button disabled={pageIndex === 0} onClick={previousPage}>
          Zurück
        </Button>

        <Button disabled={pageIndex >= pageCount - 1} onClick={nextPage}>
          Weiter
        </Button>
      </HStack>
    </>
  )
}
